import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";
import {
    ChatBubbleBottomCenterTextIcon as AnnotationIcon,
    BriefcaseIcon,
    ChartPieIcon,
    NewspaperIcon,
} from "@heroicons/react/24/solid";
import { FTPBreadCrumbs } from "../../components-new/BreadCrumbs";
import { useStore } from "../../helpers";
import { classNames } from "../../helpers/utils";
import { ArticlesView } from "./ArticlesView";
import { ReportViewFilter } from "./ReportViewFilters";
import { PrintMediaReportSummary } from "./PrintMediaReportsSummary";
import { PrintMediaReportsInsights } from "./PrintMediaReportsInsights";
import { PrintMediaReportQuotes } from "./PrintMediaReportsQuotes";

const tabs = [
    { name: "Articles", icon: NewspaperIcon },
    { name: "Summary", icon: BriefcaseIcon },
    { name: "Insights", icon: ChartPieIcon },
    { name: "Quotes", icon: AnnotationIcon },
];

const crumbs = [
    { name: "Media Dashboard", href: "/media", current: false },
    { name: "Print Media", href: "/print_media_reports", current: false },
    { name: "View Report", href: "#", current: true },
];

export const PrintMediaReportView = observer(() => {
    const { mediaStore } = useStore();
    const { reportid } = useParams<{ reportid: string }>();
    const [activeTabName, setActiveTabName] = useState("Articles");

    useEffect(() => {
        const fetchReportMeta = async () => {
            const response = await mediaStore.viewPrintMediaReport(reportid);
            mediaStore.setPMReport(response.data);
        };
        void fetchReportMeta();
    }, [reportid, mediaStore]);

    return (
        <>
            <FTPBreadCrumbs crumbs={crumbs} />
            <div className="p-4">
                <div>
                    <div className="text-3xl font-bold text-gray-800">{mediaStore.printMediaReport?.name}</div>
                    <div className="text-lg font-bold text-gray-500">Print Media Report</div>
                </div>

                <div className="sm:hidden">
                    <label htmlFor="tabs" className="sr-only">
                        Select a tab
                    </label>
                    <select
                        id="tabs"
                        name="tabs"
                        className="block w-full rounded-lg border-gray-300 p-4 focus:border-indigo-500 focus:ring-indigo-500"
                        value={activeTabName}
                        onChange={(event) => setActiveTabName(event.target.value)}
                    >
                        {tabs.map((tab) => (
                            <option key={tab.name}>{tab.name}</option>
                        ))}
                    </select>
                </div>
                <div className="hidden sm:block">
                    <div className="border-b-2 border-gray-400">
                        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                            {tabs.map((tab) => {
                                const isActive = activeTabName === tab.name;
                                return (
                                    <button
                                        key={tab.name}
                                        type="button"
                                        className={classNames(
                                            isActive
                                                ? "border-indigo-500 text-indigo-600"
                                                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                                            "group inline-flex cursor-pointer items-center border-b-2 px-1 py-2"
                                        )}
                                        onClick={() => setActiveTabName(tab.name)}
                                    >
                                        <tab.icon
                                            className={classNames(
                                                isActive ? "text-indigo-500" : "text-gray-400 group-hover:text-gray-500",
                                                "-ml-0.5 mr-2 h-8 w-8"
                                            )}
                                        />
                                        <span className="text-lg font-medium">{tab.name}</span>
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                </div>

                <ReportViewFilter />
                {activeTabName === "Articles" ? <ArticlesView /> : null}
                {activeTabName === "Summary" ? <PrintMediaReportSummary /> : null}
                {activeTabName === "Insights" ? <PrintMediaReportsInsights /> : null}
                {activeTabName === "Quotes" ? <PrintMediaReportQuotes /> : null}
            </div>
        </>
    );
});
